import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Plus, Trash2, Circle, Loader2, User, ChevronDown, ChevronRight, AlertTriangle, Download, FileSpreadsheet, FileText as FileTextIcon, History, Pencil } from 'lucide-react';
import { API, BACKEND_URL } from '../config/api';
import { enqueue as enqueueOffline } from '../lib/pitOfflineQueue';
import { AUTODROMOS } from '../lib/autodromos';

const STATUS_BADGE = {
  new: { label: 'Nueva', class: 'bg-green-700' },
  used: { label: 'Usada', class: 'bg-yellow-700' },
  discarded: { label: 'Descartada', class: 'bg-neutral-700' },
};

const POSITION_LABELS = { DI: 'Del. Izq.', DD: 'Del. Der.', TI: 'Tras. Izq.', TD: 'Tras. Der.' };

const LAPS_WARNING_THRESHOLD = 40;

const CATEGORY_LABELS = {
  turismo_pista: 'Turismo Pista',
  carshow_santafesino: 'CarShow',
  turismo_nacional: 'Turismo Nacional',
  simracing: 'Simracing',
  '': 'Sin categoría',
};

const TANDA_LABELS = {
  entrenamiento_1: 'E1', entrenamiento_2: 'E2',
  clasificacion_1: 'C1', clasificacion_2: 'C2',
  serie: 'Serie', final: 'Final',
};

const BRAND_OPTIONS = [
  { value: 'NA Carrera', label: 'NA Carrera (TP / TN / TC)' },
  { value: 'Pirelli', label: 'Pirelli (CarShow)' },
  { value: '', label: 'Otra...' },
];

const JUEGO_OPTIONS = ['', 'J1', 'J2', 'J3', 'J4', 'J5', 'J6', 'J7', 'J8'];

const emptyTire = (pilot = '', category = '') => ({
  serial: '',
  pilot,
  category,
  juego: '',
  brand: 'NA Carrera',
  brandCustom: '',
  diameter_mm: '',
  purchase_date: '',
  purchase_circuit: '',
  purchase_circuit_custom: '',
  position: '',
  status: 'new',
  notes: '',
});

// Convert a tire DB record into the form shape (handles "otro" autódromo).
const tireToForm = (t) => {
  const knownAutodromo = AUTODROMOS.includes(t.purchase_circuit || '');
  const knownBrand = ['NA Carrera', 'Pirelli', ''].includes(t.brand || '');
  return {
    serial: t.serial || '',
    pilot: t.pilot || '',
    category: t.category || '',
    juego: t.juego || '',
    brand: knownBrand ? (t.brand || '') : '',
    brandCustom: knownBrand ? '' : (t.brand || ''),
    diameter_mm: t.diameter_mm ?? '',
    purchase_date: t.purchase_date || '',
    purchase_circuit: knownAutodromo ? t.purchase_circuit : (t.purchase_circuit ? '__other__' : ''),
    purchase_circuit_custom: knownAutodromo ? '' : (t.purchase_circuit || ''),
    position: t.position || '',
    status: t.status || 'new',
    notes: t.notes || '',
  };
};

export default function TireInventory() {
  const [tires, setTires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);  // null = create, else tire_id
  const [form, setForm] = useState(emptyTire());
  const [collapsed, setCollapsed] = useState({});
  const [showNewPilot, setShowNewPilot] = useState(false);
  const [newPilotName, setNewPilotName] = useState('');
  const [newPilotCategory, setNewPilotCategory] = useState('turismo_pista');
  const [detailTire, setDetailTire] = useState(null);
  const [detailUsage, setDetailUsage] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pit/tires`, { withCredentials: true });
      setTires(res.data.tires || []);
    } catch (err) {
      toast.error('Error al cargar cubiertas');
    } finally {
      setLoading(false);
    }
  };

  // Group: category → pilot → juego → tires
  const grouped = useMemo(() => {
    const cats = {};
    for (const t of tires) {
      const c = t.category || '';
      const p = t.pilot || 'Sin piloto';
      if (!cats[c]) cats[c] = {};
      if (!cats[c][p]) cats[c][p] = [];
      cats[c][p].push(t);
    }
    const sortedCats = Object.keys(cats).sort();
    return sortedCats.map((c) => {
      const pilots = Object.keys(cats[c]).sort().map((p) => {
        const list = cats[c][p].sort((a, b) => {
          // 1) Discarded tires go to bottom
          const aOrder = a.status === 'discarded' ? 1 : 0;
          const bOrder = b.status === 'discarded' ? 1 : 0;
          if (aOrder !== bOrder) return aOrder - bOrder;
          // 2) Numeric sort by serial (ascending: 1, 2, ..., 42)
          const sa = (a.serial || '').toString();
          const sb = (b.serial || '').toString();
          const cmp = sa.localeCompare(sb, undefined, { numeric: true });
          if (cmp !== 0) return cmp;
          // 3) Tiebreak by juego
          return (a.juego || '').localeCompare(b.juego || '');
        });
        return [p, list];
      });
      return [c, pilots];
    });
  }, [tires]);

  const openNewTireDialog = (pilot, category) => {
    setEditingId(null);
    setForm(emptyTire(pilot, category));
    setShowDialog(true);
  };

  const openEditTireDialog = (tire, e) => {
    e?.stopPropagation();
    setEditingId(tire.tire_id);
    setForm(tireToForm(tire));
    setShowDialog(true);
  };

  const submit = async () => {
    if (!form.serial) return toast.error('Ingresá un n° de serie');
    if (!/^\d+$/.test(form.serial)) return toast.error('El n° de serie solo debe contener números');
    if (!form.pilot) return toast.error('Falta el piloto');
    const brandFinal = form.brand === '' ? form.brandCustom : form.brand;
    const circuitFinal = form.purchase_circuit === '__other__'
      ? (form.purchase_circuit_custom || '').trim()
      : (form.purchase_circuit || '');
    try {
      const payload = {
        serial: form.serial,
        pilot: form.pilot,
        category: form.category || '',
        juego: form.juego || '',
        brand: brandFinal || '',
        diameter_mm: form.diameter_mm === '' ? null : Number(form.diameter_mm),
        purchase_date: form.purchase_date || null,
        purchase_circuit: circuitFinal,
        position: form.position,
        status: form.status,
        notes: form.notes,
      };
      if (editingId) {
        await axios.patch(`${API}/pit/tires/${editingId}`, payload, { withCredentials: true });
        toast.success('Cubierta actualizada');
      } else {
        await axios.post(`${API}/pit/tires`, payload, { withCredentials: true });
        toast.success('Cubierta agregada');
      }
      setShowDialog(false);
      setEditingId(null);
      setForm(emptyTire());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar la cubierta');
    }
  };

  const remove = async (tire_id, e) => {
    e?.stopPropagation();
    if (!window.confirm('¿Eliminar esta cubierta del inventario?')) return;
    try {
      await axios.delete(`${API}/pit/tires/${tire_id}`, { withCredentials: true });
      toast.success('Cubierta eliminada');
      setTires(tires.filter(t => t.tire_id !== tire_id));
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const changeStatus = async (tire_id, newStatus, e) => {
    e?.stopPropagation();
    setTires((prev) => prev.map(t => t.tire_id === tire_id ? { ...t, status: newStatus } : t));
    try {
      await axios.patch(`${API}/pit/tires/${tire_id}`, { status: newStatus }, { withCredentials: true });
    } catch (err) {
      const isNetwork = !err.response || !navigator.onLine;
      if (isNetwork) {
        enqueueOffline({ method: 'PATCH', path: `/pit/tires/${tire_id}`, data: { status: newStatus } });
        toast.warning('Sin conexión — guardado local', { duration: 2000 });
      } else {
        toast.error('No se pudo actualizar');
      }
    }
  };

  const changeJuego = async (tire_id, newJuego, e) => {
    e?.stopPropagation();
    setTires((prev) => prev.map(t => t.tire_id === tire_id ? { ...t, juego: newJuego } : t));
    try {
      await axios.patch(`${API}/pit/tires/${tire_id}`, { juego: newJuego }, { withCredentials: true });
    } catch (err) {
      const isNetwork = !err.response || !navigator.onLine;
      if (isNetwork) {
        enqueueOffline({ method: 'PATCH', path: `/pit/tires/${tire_id}`, data: { juego: newJuego } });
      } else {
        toast.error('No se pudo actualizar el juego');
      }
    }
  };

  const togglePilot = (key) => setCollapsed({ ...collapsed, [key]: !collapsed[key] });

  const resetJuegos = async (pilot, category, list, e) => {
    e?.stopPropagation();
    const assigned = list.filter((t) => (t.juego || '').trim() !== '');
    if (assigned.length === 0) {
      toast.info('Este piloto no tiene juegos asignados');
      return;
    }
    if (!window.confirm(`¿Borrar la asignación de juego (J1–J8) de ${assigned.length} cubiertas de ${pilot}? El histórico de uso se mantiene.`)) return;
    // Optimistic
    setTires((prev) => prev.map((t) =>
      t.pilot === pilot && (t.category || '') === (category || '') ? { ...t, juego: '' } : t,
    ));
    let failed = 0;
    await Promise.all(assigned.map(async (t) => {
      try {
        await axios.patch(`${API}/pit/tires/${t.tire_id}`, { juego: '' }, { withCredentials: true });
      } catch (err) {
        const isNetwork = !err.response || !navigator.onLine;
        if (isNetwork) {
          enqueueOffline({ method: 'PATCH', path: `/pit/tires/${t.tire_id}`, data: { juego: '' } });
        } else {
          failed += 1;
        }
      }
    }));
    if (failed > 0) toast.error(`${failed} cubiertas no pudieron resetearse`);
    else toast.success(`Se reseteó el juego en ${assigned.length} cubiertas`);
  };

  const handleAddPilot = () => {
    const name = newPilotName.trim();
    if (!name) return toast.error('Ingresá el nombre del piloto');
    setShowNewPilot(false);
    setNewPilotName('');
    openNewTireDialog(name, newPilotCategory);
  };

  const openDetail = async (tire) => {
    setDetailTire(tire);
    setDetailUsage([]);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/pit/tires/${tire.tire_id}/usage`, { withCredentials: true });
      setDetailUsage(res.data.usage || []);
    } catch {
      toast.error('No se pudo cargar el detalle');
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadFile = async (path, suggestedName) => {
    // `window.open` does NOT carry our Authorization Bearer header (axios
    // interceptors only run on XHR requests). Fetch the file as a blob and
    // trigger an in-memory download so authenticated routes work.
    const url = `${API}/pit${path}`;
    try {
      const res = await axios.get(url, {
        responseType: 'blob',
        withCredentials: true,
      });
      // Try to parse a filename from Content-Disposition
      const cd = res.headers['content-disposition'] || '';
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      const filename = (match && decodeURIComponent(match[1])) || suggestedName;
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Error al descargar (${err.response?.status || 'sin red'})`);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-semibold">Inventario de Cubiertas</h2>
          <p className="text-xs text-gray-500">Agrupado por categoría y piloto. Las vueltas y el estado se actualizan automáticamente.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={() => downloadFile('/tires/export/xlsx', 'inventario_cubiertas.xlsx')}
            data-testid="export-xlsx-btn"
            variant="outline"
            size="sm"
            className="border-green-700 text-green-400 hover:bg-green-950"
          >
            <FileSpreadsheet className="w-4 h-4 mr-1.5" />
            Exportar XLS
          </Button>
          <Button
            onClick={() => downloadFile('/tires/export/pdf', 'inventario_cubiertas.pdf')}
            data-testid="export-pdf-btn"
            variant="outline"
            size="sm"
            className="border-red-700 text-red-400 hover:bg-red-950"
          >
            <FileTextIcon className="w-4 h-4 mr-1.5" />
            Imprimir PDF
          </Button>
          <Button
            onClick={() => setShowNewPilot(true)}
            data-testid="add-pilot-btn"
            className="bg-orange-600 hover:bg-orange-700"
          >
            <User className="w-4 h-4 mr-1.5" />
            Nuevo Piloto
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Cargando...
        </div>
      ) : grouped.length === 0 ? (
        <Card className="bg-neutral-900 border-neutral-800">
          <CardContent className="py-12 text-center text-gray-500">
            <Circle className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="mb-1">No hay cubiertas en inventario</p>
            <p className="text-xs">Tocá "Nuevo Piloto" para empezar a cargar cubiertas</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-5">
          {grouped.map(([cat, pilots]) => (
            <div key={cat}>
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-red-700 text-white text-xs">
                  {CATEGORY_LABELS[cat] !== undefined ? CATEGORY_LABELS[cat] : cat}
                </Badge>
                <div className="text-xs text-gray-600">
                  {pilots.reduce((a, [, l]) => a + l.length, 0)} cubiertas en {pilots.length} {pilots.length === 1 ? 'piloto' : 'pilotos'}
                </div>
              </div>
              <div className="space-y-3">
                {pilots.map(([pilot, list]) => {
                  const collapseKey = `${cat}::${pilot}`;
                  const isCollapsed = collapsed[collapseKey];
                  const overLimitCount = list.filter((t) => (t.total_laps ?? 0) > LAPS_WARNING_THRESHOLD && t.status !== 'discarded').length;
                  return (
                    <Card key={collapseKey} className="bg-neutral-900 border-neutral-800">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <button
                            onClick={() => togglePilot(collapseKey)}
                            className="flex items-center gap-2 text-left flex-1 min-w-0"
                            data-testid={`pilot-toggle-${pilot}`}
                          >
                            {isCollapsed ? <ChevronRight className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                            <User className="w-4 h-4 text-orange-400" />
                            <h3 className="font-bold text-white truncate">{pilot}</h3>
                            <Badge variant="outline" className="text-[10px] h-4 px-1.5 font-normal text-gray-300 border-gray-600">
                              {list.length} {list.length === 1 ? 'cubierta' : 'cubiertas'}
                            </Badge>
                            {overLimitCount > 0 && (
                              <Badge className="bg-red-700 text-[10px] h-4 px-1.5">⚠ {overLimitCount} sobre 40</Badge>
                            )}
                          </button>
                          <Button
                            size="sm"
                            onClick={() => openNewTireDialog(pilot, cat)}
                            data-testid={`new-tire-${pilot}`}
                            className="bg-orange-600 hover:bg-orange-700 h-9"
                          >
                            <Plus className="w-3.5 h-3.5 mr-1" />
                            Cubierta
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => resetJuegos(pilot, cat, list, e)}
                            data-testid={`reset-juegos-${pilot}`}
                            title="Borra la asignación J1-J8 de todas las cubiertas de este piloto"
                            className="h-9 border-yellow-700 text-yellow-400 hover:bg-yellow-950"
                          >
                            Resetear juegos
                          </Button>
                        </div>
                        {!isCollapsed && (
                          <div className="overflow-x-auto -mx-1">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-[10px] uppercase text-gray-500 font-semibold border-b border-neutral-800">
                                  <th className="text-left px-2 py-1.5 w-14">Juego</th>
                                  <th className="text-left px-2 py-1.5 w-20">N° Serie</th>
                                  <th className="text-left px-2 py-1.5 w-28">Marca</th>
                                  <th className="text-left px-2 py-1.5">Autódromo compra</th>
                                  <th className="text-left px-2 py-1.5 w-20">Pos.</th>
                                  <th className="text-right px-2 py-1.5 w-12">Ø</th>
                                  <th className="text-right px-2 py-1.5 w-16">Vtas</th>
                                  <th className="text-left px-2 py-1.5 w-28">Estado</th>
                                  <th className="text-right px-2 py-1.5 w-16"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {list.map((t) => {
                                  const laps = t.total_laps ?? 0;
                                  const overLimit = laps > LAPS_WARNING_THRESHOLD && t.status !== 'discarded';
                                  return (
                                    <tr
                                      key={t.tire_id}
                                      data-testid={`tire-row-${t.tire_id}`}
                                      onClick={() => openDetail(t)}
                                      className={`border-b border-neutral-800/40 hover:bg-neutral-800/30 cursor-pointer ${overLimit ? 'bg-red-950/20' : ''}`}
                                    >
                                      <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                                        <select
                                          value={t.juego || ''}
                                          onChange={(e) => changeJuego(t.tire_id, e.target.value, e)}
                                          className="bg-neutral-800 border border-neutral-700 text-white text-xs rounded px-1 py-1 h-7 w-full font-mono"
                                          data-testid={`tire-juego-${t.tire_id}`}
                                        >
                                          {JUEGO_OPTIONS.map((j) => (
                                            <option key={j || 'none'} value={j}>{j || '—'}</option>
                                          ))}
                                        </select>
                                      </td>
                                      <td className="px-2 py-2">
                                        <span className="font-mono font-bold text-white">#{t.serial}</span>
                                      </td>
                                      <td className="px-2 py-2 text-gray-300 truncate max-w-[120px]">{t.brand || '—'}</td>
                                      <td className="px-2 py-2 text-gray-400 truncate max-w-[200px]" title={t.purchase_circuit || ''}>
                                        {t.purchase_circuit || '—'}
                                      </td>
                                      <td className="px-2 py-2 text-gray-300 font-mono">
                                        {t.position ? POSITION_LABELS[t.position] || t.position : '—'}
                                      </td>
                                      <td className="px-2 py-2 text-right text-gray-400 font-mono">
                                        {t.diameter_mm || '—'}
                                      </td>
                                      <td className="px-2 py-2 text-right">
                                        <span className={`font-mono font-semibold inline-flex items-center gap-1 ${overLimit ? 'text-red-400' : 'text-yellow-400'}`}>
                                          {overLimit && <AlertTriangle className="w-3 h-3" />}
                                          {laps}
                                        </span>
                                      </td>
                                      <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                                        <select
                                          value={t.status}
                                          onChange={(e) => changeStatus(t.tire_id, e.target.value, e)}
                                          className={`w-full bg-neutral-800 border ${overLimit ? 'border-red-700' : 'border-neutral-700'} text-white text-xs rounded px-2 py-1 h-7`}
                                          data-testid={`tire-status-${t.tire_id}`}
                                        >
                                          <option value="new">Nueva</option>
                                          <option value="used">Usada</option>
                                          <option value="discarded">Descartada</option>
                                        </select>
                                      </td>
                                      <td className="px-2 py-2 text-right">
                                        <div className="flex items-center justify-end gap-0.5">
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={(e) => openEditTireDialog(t, e)}
                                            data-testid={`edit-tire-${t.tire_id}`}
                                            className="h-7 w-7 text-gray-500 hover:text-blue-400"
                                            title="Editar cubierta"
                                          >
                                            <Pencil className="w-3.5 h-3.5" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={(e) => remove(t.tire_id, e)}
                                            className="h-7 w-7 text-gray-600 hover:text-red-500"
                                            title="Eliminar"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </Button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            {overLimitCount > 0 && (
                              <div className="mt-2 mx-1 flex items-center gap-2 text-[11px] text-red-300 bg-red-950/30 border border-red-900/40 rounded px-2 py-1.5">
                                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                                Hay cubiertas con más de {LAPS_WARNING_THRESHOLD} vueltas. Considerá descartarlas o rotarlas.
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New Pilot dialog */}
      <Dialog open={showNewPilot} onOpenChange={setShowNewPilot}>
        <DialogContent className="bg-neutral-900 border-neutral-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Nuevo Piloto</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Elegí la categoría e ingresá el nombre del piloto
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div>
              <Label className="text-xs text-gray-400">Categoría</Label>
              <select
                value={newPilotCategory}
                onChange={(e) => setNewPilotCategory(e.target.value)}
                className="w-full bg-neutral-800 border border-neutral-700 text-white rounded-md px-3 py-2 text-sm h-10"
                data-testid="new-pilot-category"
              >
                <option value="turismo_pista">Turismo Pista</option>
                <option value="carshow_santafesino">CarShow Santafesino</option>
                <option value="turismo_nacional">Turismo Nacional</option>
                <option value="simracing">Simracing</option>
                <option value="">Otra / Sin categoría</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-400">Nombre del piloto</Label>
              <Input
                value={newPilotName}
                onChange={(e) => setNewPilotName(e.target.value)}
                placeholder="Juan Pérez"
                data-testid="new-pilot-name"
                className="bg-neutral-800 border-neutral-700 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewPilot(false)}>Cancelar</Button>
            <Button onClick={handleAddPilot} data-testid="confirm-new-pilot" className="bg-orange-600 hover:bg-orange-700">
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New / Edit Tire dialog */}
      <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) setEditingId(null); }}>
        <DialogContent className="bg-neutral-900 border-neutral-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingId ? 'Editar cubierta' : 'Nueva Cubierta'} — <span className="text-orange-400">{form.pilot}</span>
              {form.category && <Badge className="ml-2 bg-red-700 text-white text-[10px]">{CATEGORY_LABELS[form.category] || form.category}</Badge>}
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              {editingId ? 'Actualizá los datos de esta cubierta' : 'Cargá una cubierta nueva al inventario del piloto'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2 max-h-[70vh] overflow-y-auto">
            <div>
              <Label className="text-xs text-gray-400">N° serie (números) *</Label>
              <Input
                inputMode="numeric"
                pattern="[0-9]*"
                value={form.serial}
                onChange={(e) => setForm({ ...form, serial: e.target.value.replace(/\D/g, '') })}
                placeholder="042"
                data-testid="tire-serial-input"
                className="bg-neutral-800 border-neutral-700 text-white font-mono h-10"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-400">Juego</Label>
              <select
                value={form.juego}
                onChange={(e) => setForm({ ...form, juego: e.target.value })}
                className="w-full bg-neutral-800 border border-neutral-700 text-white rounded-md px-3 py-2 text-sm h-10"
                data-testid="tire-juego-select"
              >
                {JUEGO_OPTIONS.map((j) => (
                  <option key={j || 'none'} value={j}>{j || 'Sin juego'}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-gray-400">Marca</Label>
              <select
                value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })}
                className="w-full bg-neutral-800 border border-neutral-700 text-white rounded-md px-3 py-2 text-sm h-10"
                data-testid="tire-brand-select"
              >
                {BRAND_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
              {form.brand === '' && (
                <Input
                  value={form.brandCustom}
                  onChange={(e) => setForm({ ...form, brandCustom: e.target.value })}
                  placeholder="Marca personalizada"
                  className="bg-neutral-800 border-neutral-700 text-white mt-1.5"
                />
              )}
            </div>
            <div>
              <Label className="text-xs text-gray-400">Diámetro (mm)</Label>
              <Input
                type="number"
                inputMode="decimal"
                step="0.1"
                value={form.diameter_mm}
                onChange={(e) => setForm({ ...form, diameter_mm: e.target.value })}
                placeholder="585"
                className="bg-neutral-800 border-neutral-700 text-white h-10"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-400">Posición</Label>
              <select
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
                className="w-full bg-neutral-800 border border-neutral-700 text-white rounded-md px-3 py-2 text-sm h-10"
                data-testid="tire-position-select"
              >
                <option value="">Sin asignar</option>
                <option value="DI">Del. Izq. (DI)</option>
                <option value="DD">Del. Der. (DD)</option>
                <option value="TI">Tras. Izq. (TI)</option>
                <option value="TD">Tras. Der. (TD)</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-400">Fecha compra</Label>
              <Input
                type="date"
                value={form.purchase_date}
                onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
                className="bg-neutral-800 border-neutral-700 text-white h-10"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-400">Estado</Label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full bg-neutral-800 border border-neutral-700 text-white rounded-md px-3 py-2 text-sm h-10"
                data-testid="tire-status-select-form"
              >
                <option value="new">Nueva</option>
                <option value="used">Usada</option>
                <option value="discarded">Descartada</option>
              </select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-gray-400">Autódromo de compra</Label>
              <select
                value={form.purchase_circuit}
                onChange={(e) => setForm({ ...form, purchase_circuit: e.target.value })}
                className="w-full bg-neutral-800 border border-neutral-700 text-white rounded-md px-3 py-2 text-sm h-10"
                data-testid="tire-autodromo-select"
              >
                <option value="">— Sin especificar —</option>
                {AUTODROMOS.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
                <option value="__other__">Otro...</option>
              </select>
              {form.purchase_circuit === '__other__' && (
                <Input
                  value={form.purchase_circuit_custom}
                  onChange={(e) => setForm({ ...form, purchase_circuit_custom: e.target.value })}
                  placeholder="Nombre del autódromo"
                  data-testid="tire-autodromo-custom"
                  className="bg-neutral-800 border-neutral-700 text-white mt-1.5 h-10"
                />
              )}
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-gray-400">Observaciones</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Notas..."
                className="bg-neutral-800 border-neutral-700 text-white h-10"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowDialog(false); setEditingId(null); }}>Cancelar</Button>
            <Button onClick={submit} data-testid="submit-tire" className="bg-orange-600 hover:bg-orange-700">
              {editingId ? 'Guardar cambios' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tire detail dialog */}
      <Dialog open={!!detailTire} onOpenChange={(o) => !o && setDetailTire(null)}>
        <DialogContent className="bg-neutral-900 border-neutral-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              Cubierta #{detailTire?.serial}
              {detailTire?.juego && <Badge variant="outline" className="text-[10px] h-5 text-gray-300 border-gray-600">{detailTire.juego}</Badge>}
              {detailTire && (
                <Badge className={STATUS_BADGE[detailTire.status]?.class || 'bg-neutral-700'}>
                  {STATUS_BADGE[detailTire.status]?.label || detailTire.status}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Detalle e historial de uso de la cubierta
            </DialogDescription>
          </DialogHeader>
          {detailTire && (
            <div className="space-y-3 py-2 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-gray-500">Piloto:</span> <span className="text-white">{detailTire.pilot}</span></div>
                <div><span className="text-gray-500">Categoría:</span> <span className="text-white">{CATEGORY_LABELS[detailTire.category || ''] || detailTire.category || '—'}</span></div>
                <div><span className="text-gray-500">Marca:</span> <span className="text-white">{detailTire.brand || '—'}</span></div>
                <div><span className="text-gray-500">Posición:</span> <span className="text-white">{detailTire.position ? POSITION_LABELS[detailTire.position] || detailTire.position : '—'}</span></div>
                <div><span className="text-gray-500">Diámetro:</span> <span className="text-white">{detailTire.diameter_mm ? `${detailTire.diameter_mm} mm` : '—'}</span></div>
                <div><span className="text-gray-500">Compra:</span> <span className="text-white">{detailTire.purchase_date || '—'}</span></div>
                <div className="col-span-2"><span className="text-gray-500">Autódromo compra:</span> <span className="text-white">{detailTire.purchase_circuit || '—'}</span></div>
                <div className="col-span-2"><span className="text-gray-500">Observaciones:</span> <span className="text-white">{detailTire.notes || '—'}</span></div>
              </div>
              <div className="pt-3 border-t border-neutral-800">
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <History className="w-4 h-4 text-yellow-400" />
                  Historial de uso ({detailTire.total_laps ?? 0} vueltas totales)
                </h4>
                {detailLoading ? (
                  <div className="flex items-center gap-2 text-gray-500 text-xs">
                    <Loader2 className="w-4 h-4 animate-spin" /> Cargando...
                  </div>
                ) : detailUsage.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No se registró uso aún en ningún fin de semana.</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] uppercase text-gray-500 border-b border-neutral-800">
                        <th className="text-left px-2 py-1.5">Fecha</th>
                        <th className="text-left px-2 py-1.5">Circuito</th>
                        <th className="text-left px-2 py-1.5">Tanda</th>
                        <th className="text-left px-2 py-1.5">Slot</th>
                        <th className="text-right px-2 py-1.5">Vtas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailUsage.map((u, i) => (
                        <tr key={i} className="border-b border-neutral-800/40">
                          <td className="px-2 py-1.5 text-gray-300">{u.date || '—'}</td>
                          <td className="px-2 py-1.5 text-gray-300">{u.circuit || '—'}</td>
                          <td className="px-2 py-1.5 text-gray-300 font-mono">{TANDA_LABELS[u.tanda] || u.tanda}</td>
                          <td className="px-2 py-1.5 text-gray-400 font-mono">{u.slot}</td>
                          <td className="px-2 py-1.5 text-right text-yellow-400 font-mono">{u.laps}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDetailTire(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
